import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import TopNavigation from '../../../components/TopNavigation.jsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { verifyEmailCode, resendVerificationCode } from '../../lib/auth-helpers'

const VerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, refreshUser, secretUser } = useAuth()

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [message, setMessage] = useState('Enter the 6-digit code we emailed you.')
  const [errorText, setErrorText] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const urlEmail = searchParams.get('email') || ''
    if (user?.email && !email) setEmail(user.email)
    else if (urlEmail && !email) setEmail(urlEmail)
  }, [user, searchParams, email])

  const handleVerify = async () => {
    try {
      setErrorText('')
      setVerifying(true)
      if (!email) {
        setErrorText('Please enter your email.')
        setVerifying(false)
        return
      }
      if (!code || code.length !== 6) {
        setErrorText('Please enter the 6-digit code.')
        setVerifying(false)
        return
      }
      await verifyEmailCode(email, code.trim())
      setSuccess(true)
      setMessage('Email verified successfully! You can now sign in.')
      setTimeout(() => navigate('/login?t=email'), 1500)
    } catch (err: any) {
      setErrorText(err?.message || 'Invalid or expired code. Request a new one and try again.')
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    try {
      setErrorText('')
      setResending(true)
      if (!email) {
        setErrorText('Please enter your email to resend the code.')
        setResending(false)
        return
      }

      // Get user metadata for email personalization
      const userData = user?.user_metadata || {}
      const fullName = userData.full_name || 
        (secretUser?.first_name && secretUser?.last_name 
          ? `${secretUser.first_name} ${secretUser.last_name}`.trim()
          : secretUser?.first_name || '')

      await resendVerificationCode(email, {
        full_name: fullName,
        fullName: fullName, // camelCase version
        first_name: userData.first_name || secretUser?.first_name || '',
        last_name: userData.last_name || secretUser?.last_name || ''
      })
      setMessage('New verification code sent. Check your inbox and enter the code above.')
    } catch (err: any) {
      setErrorText(err?.message || 'Failed to resend code. Try again in a moment.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      <TopNavigation />

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          {success ? (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">Verification Successful</h2>
              <p className="text-muted-foreground">{message}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-foreground">Verify your email</h2>
              <p className="text-muted-foreground">{message}</p>

              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-center text-lg font-mono"
                />
              </div>

              {errorText && (
                <p className="text-xs text-red-500">{errorText}</p>
              )}

              <button
                onClick={handleVerify}
                disabled={verifying || !email || code.length !== 6}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
              >
                {verifying ? <span className="inline-flex items-center"><LoadingSpinner size="sm" /> Verifying…</span> : 'Verify Email'}
              </button>

              <button
                onClick={handleResend}
                disabled={resending || !email}
                className="border border-border text-foreground hover:bg-muted w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
              >
                {resending ? 'Sending…' : 'Send New Code'}
              </button>

              <div className="pt-2 text-sm">
                <button onClick={() => navigate('/login')} className="text-muted-foreground hover:text-foreground underline">
                  Back to Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerifyPage